import type { z } from 'zod';
import { ApiFailure, failureFromBody } from './errors';

/**
 * One thin wrapper over `fetch`, and every request in the console goes through
 * it. It is `apps/app`'s client with two things taken out and nothing added.
 *
 * **No `Idempotency-Key`.** The staff app's whole guarantee rests on one,
 * because its writes are queued on a device and replayed later. Nothing here is
 * queued: an operator action is a request, made while the operator watches, and
 * a retry is the operator clicking again with the server's answer in front of
 * them. A key would be ceremony over a decision that is already visible.
 *
 * **No optimism.** There is nothing to paint ahead of the answer, because there
 * is no local copy to paint it into. A screen shows what the last read
 * returned; an action either returns and the screen re-reads, or fails and says
 * so where the button is.
 *
 * What is kept is the one behaviour a token-bearing client needs: on a `401` it
 * forces exactly one token refresh and retries once. Firebase rotates ID tokens
 * hourly and a console tab left open over lunch will present a stale one on its
 * first request back. One retry, not a loop — a second `401` means the session
 * is genuinely over, and the shell renders that rather than hiding it.
 */

export type TokenSource = (forceRefresh?: boolean) => Promise<string | null>;

export interface RequestSpec<T> {
  // `PATCH` arrived with the Concierge panel, whose settings write is partial
  // by design: it sends only the fields the operator changed, because a body
  // that re-asserted every field would overwrite a greeting somebody edited in
  // the other console thirty seconds ago.
  // `DELETE` arrived with the tenant intake and there is exactly one of it:
  // removing a closure the console applied from an owner's answers. The verb is
  // honest — the row goes away — and the database is what bounds it to a tenant
  // that is still `onboarding` (migration 0035).
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Under `/v1`, leading slash: `/tenants`. */
  path: string;
  query?: Record<string, string | number | undefined> | undefined;
  body?: unknown;
  schema: z.ZodType<T>;
  signal?: AbortSignal | undefined;
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: TokenSource;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenSource;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.getToken = options.getToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  url(path: string, query?: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== '') search.set(key, String(value));
    }
    const qs = search.toString();
    return `${this.baseUrl}/v1${path}${qs === '' ? '' : `?${qs}`}`;
  }

  async request<T>(spec: RequestSpec<T>): Promise<T> {
    const first = await this.send(spec, false);
    const response = first.status === 401 ? await this.send(spec, true) : first;

    const text = await response.text();
    const parsedJson = parseJson(text);

    if (!response.ok) {
      throw failureFromBody(response.status, parsedJson);
    }

    const parsed = spec.schema.safeParse(parsedJson);
    if (!parsed.success) {
      // Ours to say — no service wrote this sentence, the schema did — so it
      // carries a catalog key beside the English text (`errors.ts`, `own`).
      // The issue list underneath it stays as zod produced it: those are field
      // paths, which are the same tokens in every language.
      throw new ApiFailure('malformed', 'admin-api sent a shape this console does not know.', {
        status: response.status,
        detail: { issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) },
        own: { key: 'failure.malformed' },
      });
    }
    return parsed.data;
  }

  private async send<T>(spec: RequestSpec<T>, forceRefresh: boolean): Promise<Response> {
    const token = await this.getToken(forceRefresh);

    const headers: Record<string, string> = { accept: 'application/json' };
    if (token !== null) headers['authorization'] = `Bearer ${token}`;
    if (spec.body !== undefined) headers['content-type'] = 'application/json';

    try {
      return await this.fetchImpl(this.url(spec.path, spec.query), {
        method: spec.method,
        headers,
        ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
        ...(spec.signal === undefined ? {} : { signal: spec.signal }),
      });
    } catch (e) {
      // `fetch` rejects for exactly one class of reason: the request did not
      // complete. On this surface that is not a queue-and-wait — it is a
      // sentence on the screen, because the operator is looking at it.
      throw ApiFailure.offline(String(e));
    }
  }
}

function parseJson(text: string): unknown {
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
